import React, { useEffect, useState } from 'react';
import { Search, Loader2, AlertCircle, ShieldCheck, Upload, ArrowUpRight } from 'lucide-react';
import { PermissionGuard } from '../../../hooks/usePermissions';
import { useSupplierCoverage } from '../../../hooks/queries';
import type {
  ContractCategory,
  SupplierCoverage as SupplierCoverageRow,
  SupplierCoverageFilters,
  SupplierCoverageState
} from '../../../services/api/contractService';
import { CONTRACT_CATEGORIES, categoryLabel } from '../../../utils/contractCategories';

type View = NonNullable<SupplierCoverageFilters['coverage']>;

const COVERAGE_META: Record<SupplierCoverageState, { label: string; badge: string }> = {
  none: {
    label: 'No contract',
    badge: 'bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-900/30 dark:text-rose-300 dark:border-rose-800'
  },
  expired: {
    label: 'Expired',
    badge: 'bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-900/30 dark:text-rose-300 dark:border-rose-800'
  },
  expiring: {
    label: 'Expiring soon',
    badge: 'bg-amber-50 text-amber-800 border-amber-200 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-800'
  },
  covered: {
    label: 'Covered',
    badge: 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-800'
  }
};

const inputClass =
  'w-full px-3 py-1.5 text-sm bg-white dark:bg-dark-bg-secondary border border-gray-300 dark:border-dark-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-accent-400 focus:border-primary-500 dark:focus:border-accent-400 text-gray-900 dark:text-white transition-colors';

const formatDate = (value: string) =>
  new Date(value).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' });

const inDays = (days: number) => {
  if (days === 0) return 'today';
  const abs = Math.abs(days);
  const span = abs < 60 ? `${abs} day${abs === 1 ? '' : 's'}` : `${Math.round(abs / 30.44)} months`;
  return days > 0 ? `in ${span}` : `${span} ago`;
};

/** One line saying where the supplier stands and why. */
const describe = (row: SupplierCoverageRow): string => {
  const inReview = row.unsignedContracts > 0 ? ` · ${row.unsignedContracts} in review` : '';
  if (row.coverage === 'none') {
    return row.unsignedContracts > 0 ? `No signed contract yet${inReview}` : 'No contract on file';
  }
  const end = row.latestEndDate ? formatDate(row.latestEndDate) : '';
  const when = row.daysUntilLatestEnd !== null ? ` (${inDays(row.daysUntilLatestEnd)})` : '';
  if (row.coverage === 'expired') return `Last contract ended ${end}${when}${inReview}`;
  if (row.coverage === 'expiring') return `Current contract ends ${end}${when}${inReview}`;
  return `Covered until ${end}${inReview}`;
};

interface SupplierCoverageProps {
  /** Show this supplier's contracts in the repository list. */
  onShowContracts: (supplierId: string) => void;
  /** Start adding a signed contract. */
  onAddContract: () => void;
}

/**
 * Active suppliers (the master list) and whether each has a valid signed
 * contract. Opens on the ones that don't, most urgent first.
 */
export const SupplierCoverage: React.FC<SupplierCoverageProps> = ({ onShowContracts, onAddContract }) => {
  const [view, setView] = useState<View>('missing');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState<ContractCategory | ''>('');

  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchInput.trim()), 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const { data, isLoading, isFetching, isError, error } = useSupplierCoverage({
    coverage: view,
    search: search || undefined,
    category: category || undefined
  });
  const suppliers = data?.suppliers ?? [];
  const counts = data?.counts;
  const months = data?.expiringSoonMonths ?? 6;

  const views: { key: View; label: string; count?: number }[] = [
    { key: 'missing', label: 'Without a valid contract', count: counts ? counts.none + counts.expired : undefined },
    { key: 'expiring', label: `Expiring in ${months} months`, count: counts?.expiring },
    { key: 'covered', label: 'Covered', count: counts?.covered },
    { key: 'all', label: 'All active', count: counts?.total }
  ];

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-600 dark:text-gray-400">
        Every active supplier and whether a valid signed contract is on file. A contract still in review doesn't count
        until its signed copy is added.
      </p>

      <div className="bg-white dark:bg-dark-surface p-4 sm:p-5 rounded-xl border border-gray-100 dark:border-dark-border shadow-sm space-y-4">
        <div role="tablist" aria-label="Coverage" className="flex flex-wrap gap-1 p-1 bg-gray-100 dark:bg-dark-bg-secondary rounded-lg w-full sm:w-fit">
          {views.map((v) => (
            <button
              key={v.key}
              type="button"
              role="tab"
              aria-selected={view === v.key}
              onClick={() => setView(v.key)}
              className={`flex-1 sm:flex-none px-3 py-1.5 text-sm font-medium rounded-md whitespace-nowrap transition-colors ${
                view === v.key
                  ? 'bg-white dark:bg-dark-surface text-gray-900 dark:text-white shadow-sm'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
              }`}
            >
              {v.label}
              {v.count !== undefined && <span className="ml-1.5 tabular-nums text-gray-400 dark:text-gray-500">{v.count}</span>}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="sm:col-span-2">
            <label htmlFor="coverage-search" className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
              Supplier
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 dark:text-gray-500" />
              <input
                id="coverage-search"
                type="search"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Search suppliers…"
                className={`${inputClass} pl-9 placeholder-gray-500 dark:placeholder-gray-400`}
              />
            </div>
          </div>
          <div>
            <label htmlFor="coverage-category" className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
              Category
            </label>
            <select
              id="coverage-category"
              value={category}
              onChange={(e) => setCategory(e.target.value as ContractCategory | '')}
              className={inputClass}
            >
              <option value="">All categories</option>
              {CONTRACT_CATEGORIES.map((c) => (
                <option key={c.value} value={c.value}>{c.label}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className="bg-white dark:bg-dark-surface rounded-xl border border-gray-100 dark:border-dark-border shadow-sm overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center gap-2 py-16 text-sm text-gray-500 dark:text-gray-400">
            <Loader2 className="w-4 h-4 animate-spin" aria-hidden />
            Checking suppliers…
          </div>
        ) : isError ? (
          <div role="alert" className="flex flex-col items-center gap-2 py-16 text-center">
            <AlertCircle className="w-8 h-8 text-rose-500" aria-hidden />
            <p className="text-sm font-medium text-gray-900 dark:text-white">Couldn't check supplier coverage</p>
            <p className="text-sm text-gray-500 dark:text-gray-400">{error?.message}</p>
          </div>
        ) : suppliers.length === 0 ? (
          <div className="flex flex-col items-center gap-3 py-16 px-4 text-center">
            <ShieldCheck className="w-10 h-10 text-emerald-500" aria-hidden />
            <p className="text-sm font-medium text-gray-900 dark:text-white">
              {search || category
                ? 'No suppliers match these filters'
                : view === 'missing'
                  ? 'Every active supplier has a valid contract'
                  : view === 'expiring'
                    ? `No contracts end in the next ${months} months`
                    : 'No suppliers here'}
            </p>
          </div>
        ) : (
          <ul className={`divide-y divide-gray-100 dark:divide-dark-border transition-opacity ${isFetching ? 'opacity-60' : ''}`}>
            {suppliers.map((row) => {
              const signed = row.activeContracts + row.expiringSoonContracts + row.expiredContracts;
              const cat = categoryLabel(row.defaultCategory) || row.contractCategories.map(categoryLabel).filter(Boolean).join(', ');
              return (
                <li key={row._id} className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 px-4 py-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{row.companyName}</p>
                      <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full border whitespace-nowrap ${COVERAGE_META[row.coverage].badge}`}>
                        {COVERAGE_META[row.coverage].label}
                      </span>
                    </div>
                    <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                      {[cat, row.currencies.join(' / ')].filter(Boolean).join(' · ')}
                      {(cat || row.currencies.length > 0) && ' · '}
                      {describe(row)}
                    </p>
                  </div>
                  <div className="flex items-center gap-1 sm:justify-end">
                    {signed > 0 && (
                      <button
                        type="button"
                        onClick={() => onShowContracts(row._id)}
                        className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-dark-surface-raised transition-colors"
                      >
                        {signed} contract{signed === 1 ? '' : 's'}
                        <ArrowUpRight className="w-3.5 h-3.5" aria-hidden />
                      </button>
                    )}
                    {row.coverage !== 'covered' && (
                      <PermissionGuard permission="contracts.files.upload">
                        <button
                          type="button"
                          onClick={onAddContract}
                          aria-label={`Add a signed contract for ${row.companyName}`}
                          className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-primary-700 dark:text-accent-400 rounded-lg hover:bg-primary-50 dark:hover:bg-dark-surface-raised transition-colors"
                        >
                          <Upload className="w-3.5 h-3.5" aria-hidden />
                          Add signed contract
                        </button>
                      </PermissionGuard>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
};

export default SupplierCoverage;
